/**
 * Builds the pre-canned offer message an analyst sends to a candidate NP:
 * a short scope summary for the assignment plus the fee and turnaround,
 * ending with "can you take this on?". Pure + unit-tested; the analyst
 * can edit the seeded text before sending.
 */

import type { BackofficeAssignment } from '@/types/np-assignments';

function scopeLine(item: BackofficeAssignment['items'][number]): string {
  const parts = [item.scopeComponentName || item.scopeComponentSlug];
  if (item.subjectName) {
    parts.push(item.subjectName);
  }
  if (item.jurisdictionName) {
    parts.push(item.jurisdictionName);
  }
  return `- ${parts.join(' · ')}`;
}

export function buildNpOfferMessage(
  assignment: BackofficeAssignment,
  npName: string | null
): string {
  const greeting = npName ? `Hi ${npName},` : 'Hi,';

  const scope =
    assignment.items.length > 0
      ? assignment.items.map(scopeLine).join('\n')
      : '- (scope to be confirmed)';

  const feeBits: string[] = [];
  if (assignment.npFee != null) {
    const currency = assignment.npCurrency ? `${assignment.npCurrency} ` : '';
    feeBits.push(`Fee: ${currency}${assignment.npFee}`);
  }
  if (assignment.npDueDate) {
    feeBits.push(`Due: ${assignment.npDueDate}`);
  } else if (assignment.turnaroundTimeDays != null) {
    feeBits.push(`Turnaround: ${assignment.turnaroundTimeDays} day(s)`);
  }
  const terms = feeBits.length > 0 ? `\n\n${feeBits.join('  ·  ')}` : '';

  return (
    `${greeting}\n\n` +
    `We have some work we'd like to offer you:\n\n` +
    `${scope}` +
    `${terms}\n\n` +
    `Are you able to take this on? Happy to discuss the fee and timing here.\n\n` +
    `Thanks.`
  );
}
